import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Combobox,
  Field,
  Option,
  Spinner,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  MIN_CLIENT_SEARCH_LENGTH,
  useClient,
  useClientSearch,
} from '../services/conversionsApi';
import type { ClientSummary } from '../types';
import { isAuthRequiredError } from '../lib/authError';

const useStyles = makeStyles({
  selected: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },
  selectedName: {
    fontWeight: tokens.fontWeightSemibold,
  },
  selectedDba: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  optionPrimary: {
    display: 'block',
  },
  optionSecondary: {
    display: 'block',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  status: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
  },
  orphan: {
    color: tokens.colorPaletteDarkOrangeForeground1,
    fontSize: tokens.fontSizeBase200,
  },
  // The header's one-line form: the search takes the whole field, and a picked name wraps rather than clipping.
  compactField: { width: '100%' },
  compactBox: { width: '100%', minWidth: 0 },
});

/** Debounce so a fast typist produces one query, not one per keystroke.
 *  Exported so tests advance the real interval rather than a copy of it that can drift. */
export const SEARCH_DEBOUNCE_MS = 300;

export interface ClientPickerProps {
  /** The currently selected client id (Dataverse account GUID), or '' for none. */
  value: string;
  onChange: (clientId: string, client: ClientSummary | null) => void;
  /** Overrides the default "Client" label. */
  label?: string;
  disabled?: boolean;
  /** Renders the field as required. */
  required?: boolean;
  /** The header's form: the label beside the field, no hint line, the search as wide as its container. */
  compact?: boolean;
}

/**
 * Search-by-company-name client lookup — the control that replaced every
 * free-text GUID field (CONVERSIONS_SPEC.md §3).
 *
 * Rules it enforces, all of them deliberate:
 * - The field shows the **company name**, never the GUID. The id is resolved
 *   behind the control and handed to `onChange`.
 * - A pasted GUID is treated as a search term, matches nothing, and produces
 *   guidance ("that's an internal id — search by company name") rather than a
 *   silent empty list.
 * - A stored id that no longer resolves renders as a visible orphan marker
 *   with the last 4 of the GUID — never hidden, never silently dropped.
 *
 * ⚠ No `aria-label` / `title` prop is passed to the Combobox. A controlled
 * Fluent v9 popup control with `aria-label` render-loops on mount in this
 * fleet; the accessible name comes from the wrapping `<Field label>`.
 */
export default function ClientPicker({
  value,
  onChange,
  label,
  disabled,
  required,
  compact = false,
}: ClientPickerProps) {
  const styles = useStyles();
  const { t } = useTranslation();

  const [term, setTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedTerm(term), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [term]);

  const search = useClientSearch(debouncedTerm);
  const selected = useClient(value);

  const options = useMemo(() => search.data?.clients ?? [], [search.data]);
  const trimmed = debouncedTerm.trim();
  const isSearching = trimmed.length >= MIN_CLIENT_SEARCH_LENGTH && search.isFetching;
  const pastedGuid = search.data?.notice === 'looksLikeGuid';

  /**
   * A failure we already know about — whether react-query has given up (`error`) or is
   * spending another attempt on it (`failureReason`).
   *
   * 🔴 **The two are different states and only reading one of them is BL-147.** query-core
   * sets `failureReason` on a failure it is about to retry and only sets `error` once it
   * stops retrying. So between the first failure and the last, a query reports
   * `isFetching: true` / `isError: false` / `error: null` — identical, from this component's
   * side, to a fetch that is simply still running.
   *
   * That window is not brief on the path this exists for: an auth failure means MSAL's
   * silent renewal timed out in a hidden iframe, and the old blanket `retry: 1` bought a
   * second one. The user watched "Searching…" for both, and the message written for exactly
   * this situation never appeared. `lib/queryRetry.ts` removes the second timeout; this
   * removes the silence during the first.
   */
  const failure = search.error ?? search.failureReason;

  function handleSelect(clientId: string) {
    const picked = options.find(o => o.clientId === clientId) ?? null;
    setTerm('');
    setDebouncedTerm('');
    onChange(clientId, picked);
  }

  function handleClear() {
    setTerm('');
    setDebouncedTerm('');
    onChange('', null);
  }

  // ── Selected state: show the name, offer "change" ──────────────────────────
  if (value) {
    const client = selected.data;
    return (
      <Field
        label={label ?? t('clientPicker.label')}
        required={required}
        orientation={compact ? 'horizontal' : 'vertical'}
        className={compact ? styles.compactField : undefined}
      >
        <div className={styles.selected}>
          {selected.isLoading ? (
            <Spinner size="tiny" label={t('common.loading')} />
          ) : client ? (
            <>
              <Text className={styles.selectedName}>{client.name}</Text>
              {client.dbaName && (
                <Text className={styles.selectedDba}>({client.dbaName})</Text>
              )}
              {!client.isActive && (
                <Text className={styles.orphan}>{t('clientPicker.inactive')}</Text>
              )}
            </>
          ) : (
            // Unresolvable id — surfaced, not swallowed. The last 4 characters
            // are enough for a human to trace it without showing a raw GUID.
            <Text className={styles.orphan}>
              {t('clientPicker.unknownClient', { suffix: value.slice(-4) })}
            </Text>
          )}
          <Button size="small" appearance="subtle" onClick={handleClear} disabled={disabled}>
            {t('clientPicker.change')}
          </Button>
        </div>
      </Field>
    );
  }

  // ── Search state ───────────────────────────────────────────────────────────
  return (
    <Field
      label={label ?? t('clientPicker.label')}
      required={required}
      hint={compact ? undefined : t('clientPicker.hint')}
      orientation={compact ? 'horizontal' : 'vertical'}
      className={compact ? styles.compactField : undefined}
    >
      <Combobox
        freeform
        clearable
        className={compact ? styles.compactBox : undefined}
        disabled={disabled}
        placeholder={t('clientPicker.placeholder')}
        value={term}
        selectedOptions={[]}
        onChange={e => setTerm(e.target.value)}
        onOptionSelect={(_e, data) => {
          if (data.optionValue) handleSelect(data.optionValue);
        }}
      >
        {options.map(client => (
          <Option key={client.clientId} value={client.clientId} text={client.name}>
            <span>
              <span className={styles.optionPrimary}>{client.name}</span>
              {client.dbaName && (
                <span className={styles.optionSecondary}>{client.dbaName}</span>
              )}
            </span>
          </Option>
        ))}
      </Combobox>

      {pastedGuid ? (
        <Text className={styles.error}>{t('clientPicker.looksLikeGuid')}</Text>
      ) : failure ? (
        // ⚠ Ordered AHEAD of the spinner deliberately, and a test pins it. A spinner shown
        // beside a failure the code has already seen is not a neutral default — it tells the
        // user to keep waiting for something that is not coming.
        (
        // 🔴 These two used to be one message, and the one they shared blamed the wrong
        // system. An expired sign-in meant the request was sent WITHOUT an Authorization
        // header, the API answered 401, and this rendered "the client directory could not
        // be reached" — a claim about Dataverse. On 2026-08-13 that sent a whole triage
        // session through the service principal, token acquisition and the Dataverse query,
        // all of which were healthy, because all of them sit past an auth check the request
        // never reached. The request is no longer sent at all; the copy now says so.
        <Text className={styles.error}>
          {isAuthRequiredError(failure)
            ? t('clientPicker.authFailed')
            : t('clientPicker.errorLoading')}
        </Text>
        )
      ) : isSearching ? (
        <Text className={styles.status}>{t('clientPicker.searching')}</Text>
      ) : trimmed.length > 0 && trimmed.length < MIN_CLIENT_SEARCH_LENGTH ? (
        <Text className={styles.status}>{t('clientPicker.keepTyping')}</Text>
      ) : trimmed.length >= MIN_CLIENT_SEARCH_LENGTH && options.length === 0 ? (
        <Text className={styles.status}>{t('clientPicker.noMatches', { term: trimmed })}</Text>
      ) : null}
    </Field>
  );
}
